import React, { useState, useEffect, useCallback } from 'react';

// Data Health — is live data flowing, and which DevSpace is (or isn't) pulling it?
// 1. Feed freshness: when each cache was last written (read straight from the cache).
// 2. DevSpaces: one row per DevSpace from its heartbeat (backend/devspace-heartbeat.sh,
//    written every sync-loop pass): who, which host, code commit, loops running, and
//    what FCLM actually answers to its login (PPR + Attendance via mcscli and curl).

const SITES = ['OLM1', 'SBD3', 'SCK8'];

function ptParts() {
  const p = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    .formatToParts(new Date()).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
  return { date: `${p.year}-${p.month}-${p.day}`, hour: Number(p.hour) % 24 + Number(p.minute) / 60 };
}
function addDays(iso, n) { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
// Current shift: day 06:00-18:30 PT; before 06:00 belongs to the prior day's night.
function currentShift() {
  const { date, hour } = ptParts();
  if (hour >= 6 && hour < 18.5) return { date, shift: 'day' };
  return { date: hour < 6 ? addDays(date, -1) : date, shift: 'night' };
}
const ageMin = (iso) => { const t = Date.parse(iso || ''); return Number.isFinite(t) ? (Date.now() - t) / 60000 : null; };
const fmtAge = (m) => (m == null ? '—' : m < 1 ? 'just now' : m < 90 ? `${Math.round(m)} min ago` : m < 48 * 60 ? `${(m / 60).toFixed(1)} h ago` : `${Math.round(m / 1440)} d ago`);
const fmtPT = (iso) => { const t = Date.parse(iso || ''); return Number.isFinite(t) ? new Date(t).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'; };

const GREEN = '#2e7d32', AMBER = '#ef6c00', RED = '#c62828', GREY = '#9e9e9e';
const dot = (c) => <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: c, marginRight: 6 }} />;

export default function DataHealth({ api }) {
  const [feeds, setFeeds] = useState([]);
  const [devspaces, setDevspaces] = useState([]);
  const [loadedAt, setLoadedAt] = useState(null);
  const [loading, setLoading] = useState(false);

  const get = useCallback((site, date, shift) =>
    fetch(`${api}/api/fclm/cache?site=${site}&date=${encodeURIComponent(date)}&shift_type=${shift}`).then(r => r.json()).catch(() => null), [api]);

  const load = useCallback(async () => {
    setLoading(true);
    const { date, shift } = currentShift();
    // feed name, cache key, ok-if-younger-than (min), amber-if-younger-than (min)
    const defs = [
      ...SITES.map(s => ({ name: `Hourly ${s} (${date} ${shift})`, site: s, date, shift, warn: 35, bad: 60, hourly: true })),
      { name: `Roll Call onsite (${date} ${shift})`, site: 'OLM1', date: `ONSITE_OLM1_${date}`, shift, warn: 35, bad: 60 },
      { name: `IDRT scans (${date} ${shift})`, site: 'OLM1', date: `EOL_SCANS_${date}`, shift, warn: 35, bad: 60 },
      ...SITES.map(s => ({ name: `ALPS ${s}`, site: s, date: `ALPS_${s}`, shift: 'data', warn: 90, bad: 180 })),
      ...SITES.map(s => ({ name: `IPT ${s}`, site: s, date: `IPT_${s}`, shift: 'data', warn: 90, bad: 180 })),
      { name: 'Roster (daily)', site: 'OLM1', date: 'ROSTER_OLM1', shift: 'data', warn: 26 * 60, bad: 50 * 60 },
    ];
    const rows = await Promise.all(defs.map(async d => {
      const x = await get(d.site, d.date, d.shift);
      const at = x && (x.fetched_at || x.generatedAt);
      const extra = {};
      if (d.hourly && x && x.fclm_ok && Array.isArray(x.hour_labels)) {
        const okHours = x.hour_labels.filter(h => x.fclm_ok[h]);
        const failed = x.hour_labels.filter(h => x.fclm_ok[h] === false && (x.labor || []).some(l => (l.hours || {})[h] > 0));
        extra.detail = `${okHours.length} hour(s) fetched OK${failed.length ? ` · flagged: ${failed.join(', ')}` : ''}`;
      }
      return { ...d, at, age: ageMin(at), ...extra };
    }));
    setFeeds(rows);

    const idx = await get('OLM1', 'HEARTBEATS', 'index');
    const hosts = Object.keys((idx && idx.hosts) || {});
    const hbs = (await Promise.all(hosts.map(h => get('OLM1', `HEARTBEAT_${h}`, 'data')))).filter(h => h && h.host);
    hbs.sort((a, b) => String(b.ts).localeCompare(String(a.ts)));
    setDevspaces(hbs);
    setLoadedAt(new Date());
    setLoading(false);
  }, [get]);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const feedColor = (r) => (r.age == null ? GREY : r.age <= r.warn ? GREEN : r.age <= r.bad ? AMBER : RED);
  // A DevSpace is "pulling" if its heartbeat is fresh, the sync loop runs and FCLM answers PPR.
  const dsStatus = (h) => {
    const a = ageMin(h.ts);
    const ppr = h.probes?.ppr || {};
    const pprOk = ppr.mcscli?.result === 'ok' || ppr.curl?.result === 'ok';
    if (a == null || a > 45) return { c: GREY, t: 'No recent heartbeat (DevSpace stopped or loop down)' };
    if (!(h.loops?.syncLoop > 0)) return { c: RED, t: 'Sync loop not running' };
    const jar = h.jar || {};
    if (jar.real && jar.copy && !jar.real.session && !jar.copy.session) return { c: RED, t: 'No Midway session in the cookie jar (expired or overwritten) — run mwinit -o' };
    if (!pprOk) return { c: RED, t: `FCLM refused (${ppr.curl?.result || '?'}${ppr.curl?.http ? ` ${ppr.curl.http}` : ''}) — run mwinit -o` };
    if (jar.real && jar.copy && !jar.real.session && jar.copy.session) return { c: AMBER, t: `Cookie jar was overwritten — pulling on JarJar's protected copy until ${fmtExp(jar.copy.sessionExp)}; run mwinit -o` };
    if (jar.copy?.sessionExp && jar.copy.sessionExp * 1000 - Date.now() < 2 * 3600 * 1000) return { c: AMBER, t: `Pulling — Midway session expires ${fmtExp(jar.copy.sessionExp)}; run mwinit -o before then` };
    const att = h.probes?.attendance || {};
    if (!(att.mcscli?.result === 'ok' || att.curl?.result === 'ok')) return { c: AMBER, t: 'PPR ok, Attendance refused (Roll Call won\'t update)' };
    return { c: GREEN, t: 'Pulling' };
  };
  const fmtExp = (sec) => (sec ? new Date(sec * 1000).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
  // Midway jar health: the protected copy (what the pulls read) and the real ~/.midway/cookie.
  const jarCell = (h) => {
    const j = h.jar;
    if (!j || !j.copy) return h.cookie?.exists ? `${h.cookie.ageHours} h old` : 'none';
    const one = (k, v) => `${k} ${v?.session ? `✅ until ${fmtExp(v.sessionExp)}` : '❌ no session'}`;
    return <span>{one('copy', j.copy)}<br />{one('real', j.real)}</span>;
  };
  const probeCell = (p) => {
    if (!p) return '—';
    const one = (k) => { const v = p[k]; if (!v) return `${k} —`; return `${k} ${v.result === 'ok' ? '✅' : v.result === 'n/a' ? 'n/a' : `❌ ${v.result}${v.http ? ` ${v.http}` : ''}`}`; };
    return <span title={[p.mcscli?.sample, p.curl?.sample].filter(Boolean).join(' | ')}>{one('mcscli')}<br />{one('curl')}</span>;
  };

  const th = { padding: '5px 8px', background: '#37474f', color: '#fff', fontSize: '0.72rem', textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '5px 8px', borderBottom: '1px solid #e0e0e0', fontSize: '0.74rem', verticalAlign: 'top' };
  const pulling = devspaces.filter(h => dsStatus(h).c === GREEN);

  return (
    <div>
      <div className="card">
        <h2 className="section-title">🩺 Data Health</h2>
        <div style={{ fontSize: '0.8rem', marginBottom: 10, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: pulling.length ? GREEN : RED }}>
            {dot(pulling.length ? GREEN : RED)}
            {pulling.length ? `${pulling.length} DevSpace(s) pulling live data: ${pulling.map(h => `${h.host} (${h.user})`).join(', ')}` : 'No DevSpace is currently pulling live data'}
          </span>
          <button className="btn btn-secondary" onClick={load} style={{ fontSize: '0.7rem', padding: '2px 8px' }}>↻ Refresh</button>
          <span style={{ color: '#888', fontSize: '0.7rem' }}>{loading ? 'loading…' : loadedAt ? `updated ${loadedAt.toLocaleTimeString()} · auto-refresh 60s` : ''}</span>
        </div>

        <h3 className="section-title" style={{ fontSize: '0.9rem' }}>Feed freshness</h3>
        <table style={{ borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead><tr><th style={th}>Feed</th><th style={th}>Last written</th><th style={th}>Age</th><th style={th}>Detail</th></tr></thead>
          <tbody>
            {feeds.map(r => (
              <tr key={r.name}>
                <td style={td}>{dot(feedColor(r))}{r.name}</td>
                <td style={td}>{fmtPT(r.at)}</td>
                <td style={{ ...td, color: feedColor(r), fontWeight: 600 }}>{fmtAge(r.age)}</td>
                <td style={{ ...td, color: '#666' }}>{r.detail || ''}</td>
              </tr>))}
          </tbody>
        </table>

        <h3 className="section-title" style={{ fontSize: '0.9rem' }}>DevSpaces</h3>
        {devspaces.length === 0 ? <p style={{ fontSize: '0.78rem', color: '#888' }}>No heartbeats yet. They appear once a DevSpace runs the updated backend.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr>{['Status', 'DevSpace', 'User', 'Heartbeat', 'Code', 'Loops (srv/sync/slack/mp)', 'Midway session', 'PPR', 'Attendance', 'Last FCLM OK', 'Last FCLM fail', 'Roll Call', 'Self-update'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {devspaces.map(h => { const st = dsStatus(h); const f = h.feeds || {}; return (
                  <tr key={h.host}>
                    <td style={{ ...td, color: st.c, fontWeight: 700, maxWidth: 200 }}>{dot(st.c)}{st.t}</td>
                    <td style={td}>{h.host}<div style={{ color: '#999', fontSize: '0.65rem' }}>{h.devspaceId}</div></td>
                    <td style={td}>{h.user}</td>
                    <td style={td}>{fmtAge(ageMin(h.ts))}<div style={{ color: '#999', fontSize: '0.65rem' }}>{fmtPT(h.ts)} · {h.source}</div></td>
                    <td style={{ ...td, maxWidth: 180 }}>{h.commit || '—'}</td>
                    <td style={td}>{['server', 'syncLoop', 'slackLoop', 'mpLoop'].map(k => h.loops?.[k] ?? '—').join(' / ')}</td>
                    <td style={td} title="Real = ~/.midway/cookie; copy = JarJar's protected copy (midway-jar.sh), which the pulls read">{jarCell(h)}</td>
                    <td style={td}>{probeCell(h.probes?.ppr)}</td>
                    <td style={td}>{probeCell(h.probes?.attendance)}</td>
                    <td style={{ ...td, maxWidth: 200 }}>{f.fclm?.lastOk?.at || '—'}</td>
                    <td style={{ ...td, maxWidth: 220 }} title={f.fclm?.lastFail?.line || ''}>{f.fclm?.lastFail?.at || '—'}</td>
                    <td style={{ ...td, maxWidth: 220 }} title={f.rollcall?.lastStatus || ''}>{f.rollcall ? `${f.rollcall.ok ? '✅' : '❌'} ${fmtAge(ageMin(f.rollcall.modified))}` : '—'}</td>
                    <td style={{ ...td, maxWidth: 240, color: '#666' }}>{(f.selfUpdate?.last?.line || '—').slice(0, 110)}</td>
                  </tr>); })}
              </tbody>
            </table>
          </div>)}
        <p style={{ fontSize: '0.66rem', color: '#888', marginTop: 10 }}>
          Feeds: green = written recently (Hourly / Roll Call / IDRT within 35 min; ALPS / IPT within 90 min), amber = slipping, red = stale. "Flagged" hours were last pulled unsuccessfully and are re-fetched on the next good pass.
          DevSpaces: each running DevSpace reports every sync pass (~25 min). PPR / Attendance show what FCLM answers to that DevSpace's login right now; ❌ auth 401 means the login is expired or refused — run <code>mwinit -o</code> there. Grey = no heartbeat for 45+ min (DevSpace stopped). Hover a cell for the raw response or log line.
        </p>
      </div>
    </div>
  );
}
